import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { getFieldPractices, getFieldPracticesAdmin } from "services";

import { getEnvironment } from "utils";

import { GET_FIELD_PRACTICES } from "consts";

import { FieldPracticeResponse } from "models";

type QueryFnParams = {
  fieldId: string;
  isAdmin?: boolean;
};

const env = getEnvironment();

export const useGetFieldPractices = ({
  fieldId,
  inView,
  isAdmin = env.admin,
}: QueryFnParams & {
  inView: boolean;
}) => {
  // Not having { useSuspense: false } caused unintended UI behavior — like a drawer opening and closing on mount,
  // due to remounting triggered by Suspense fallback.
  // DO NOT REMOVE unless you're explicitly handling Suspense with fallback UIs.
  const { t } = useTranslation(["practices"], { useSuspense: false });

  const fetchPractices = () => fetchFieldPracticesWithCropNames({ fieldId, isAdmin, t });

  return useQuery<FieldPracticeResponse>({
    queryKey: [GET_FIELD_PRACTICES, fieldId, isAdmin],
    queryFn: fetchPractices,
    enabled: inView && !!fieldId,
    staleTime: 1000 * 60 * 60 * (isAdmin ? 2 : 24), // 1 day for user application, 2 hours for admin
  });
};

// Shared by every query on [GET_FIELD_PRACTICES, fieldId, isAdmin], so the cached shape is always the same.
export const fetchFieldPracticesWithCropNames = async ({
  fieldId,
  isAdmin,
  t,
}: QueryFnParams & { t: (key: string) => string }): Promise<FieldPracticeResponse> => {
  const fieldPracticeData = await queryData({ fieldId, isAdmin });

  // Why is it `cropName` here and not `crop.name` like everywehre else :D
  const cropsWithInjectedNames = fieldPracticeData.crops.map((crop) => ({ ...crop, cropName: t(`crops.${crop.translationSlug}`) }));

  return {
    ...fieldPracticeData,
    crops: cropsWithInjectedNames,
  };
};

const queryData = ({ fieldId, isAdmin }: QueryFnParams) => {
  if (isAdmin) {
    return getFieldPracticesAdmin({ fieldId });
  }

  return getFieldPractices({ fieldId });
};
